/**
 * THE CURRENT PLAN — the latest succeeded run (`SPEC-APP.md` §2.7), as the SERVER resolves it.
 *
 * This used to ask `listPlanningRuns?state=succeeded&limit=1` and take the first item, which made
 * the server's default `createdAt desc` ordering an unwritten contract between three unrelated
 * hooks: change the default order and three screens quietly name a different run. `GET
 * /app/planning/runs/current` answers the question directly and owns the rule.
 *
 * NO CURRENT PLAN IS A 404, and a 404 here is not an error: a tenant before its first successful
 * run is the ordinary first-run state, which `ReportFrame` renders as an empty state. So the 404
 * becomes `null` inside the query rather than an error the callers each have to re-classify.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "../api";
import type { PlanningRun } from "../client";
import { isNotFound } from "../errors";
import { queryKeys } from "../queryKeys";
import { useBootstrap } from "./useBootstrap";
import { usePlanningScope } from "./usePlanningScope";

export function useCurrentPlan() {
  const { data: boot } = useBootstrap();
  const tenantKey = boot?.tenant.key ?? "";
  /* The current plan is PER SCOPE (PS-D6): the selected scope's latest succeeded live run. */
  const { scopeId, requestParams } = usePlanningScope();

  const query = useQuery({
    queryKey: queryKeys.runs.current(tenantKey, scopeId),
    enabled: tenantKey.length > 0,
    queryFn: async (): Promise<PlanningRun | null> => {
      try {
        return await api.call("getCurrentPlanningRun", { query: requestParams });
      } catch (error) {
        /* "No succeeded run yet" is an answer, not a failure. Anything else still throws. */
        if (isNotFound(error)) return null;
        throw error;
      }
    },
  });

  const currentPlan: PlanningRun | null = query.data ?? null;
  return { ...query, currentPlan };
}
